/* eslint-disable sonarjs/no-duplicate-string */
import { DialogActions } from 'DialogActions';
import { DialogContent } from 'DialogContent';
import { Button } from 'Button';
import { Checkbox } from 'Checkbox';

import { StyledDialog, StyledDialogTitle, StyledFormControlLabel } from './SimpleDialog.styled';
import { SimpleDialogProps } from './SimpleDialog.types';

/**
 * The SimpleDialog component is meant to handle ~95% of Dialog use cases.
 * Most dialogs that developers are asked to use should roughly match the samples from the Global Components Figma
 * @ https://www.figma.com/file/fdBNf1jsPXhceYNCxh8NVW/Global-components?node-id=7640%3A26175
 *
 * SimpleDialog can accommodate uses such as:
 * - dialogs with or without a title
 * - dialogs with or without a checkbox
 * - dialogs with or without a cancel button (only show confirm button)
 * - different "confirm" button colors ('primary' or 'danger')
 *
 * Additionally, it exposes the underlying props for each building block, such as dialogActionProps, if developers need further customization.
 *
 * With that said, developers will inevitably find use cases that this component is not written to handle.
 * For those cases, developers SHOULD NOT MODIFY THIS COMPONENT.
 * Instead, they should create a new component in their application that uses the underlying building blocks (Dialog, DialogTitle, DialogContent, etc).
 *
 * To be as clear as possible, this component is CLOSED FOR MODIFICATION.
 * Bug fixes are welcome, but we want to avoid trying to make this component handle 100% of use cases via a constantly growing number of props and boolean logic.
 *
 * The SimpleDialog will be easier to maintain AND consume if developers rely on it for common uses,
 * and build their own Dialogs in their application when they encounter a use case this component doesnt handle.
 */
export const SimpleDialog = ({
  cancelButtonProps,
  cancelText,
  checkboxLabelText,
  checkboxProps,
  children,
  confirmButtonProps,
  confirmText,
  dialogActionProps,
  dialogContentProps,
  dialogProps,
  dialogTitleProps,
  formControlLabelProps,
  open,
  title,
  ...rest
}: SimpleDialogProps) => {
  return (
    <StyledDialog
      PaperProps={{
        ['aria-modal']: true,
      }}
      {...rest}
      {...dialogProps}
      aria-label={dialogProps?.['aria-label']}
      aria-labelledby={dialogTitleProps?.id}
      open={open}
    >
      {title && (
        <StyledDialogTitle {...dialogTitleProps} id={dialogTitleProps?.id}>
          {title}
        </StyledDialogTitle>
      )}
      <DialogContent {...dialogContentProps}>
        {children}
        {checkboxLabelText && (
          <StyledFormControlLabel
            label={checkboxLabelText}
            {...formControlLabelProps}
            control={<Checkbox {...checkboxProps} />}
          />
        )}
      </DialogContent>
      <DialogActions {...dialogActionProps}>
        {cancelText && (
          <Button {...cancelButtonProps} color="secondary" variant="contained">
            {cancelText}
          </Button>
        )}
        <Button {...confirmButtonProps} variant="contained">
          {confirmText}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
